/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, Video, Hash, Clock, ChevronLeft, ChevronRight, Copy, Check, X, CheckCircle, Info, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import isEmail from 'validator/lib/isEmail';
import apiClient from '../api/client';
import PhonePreview from '../components/PhonePreview';
import QRCodeDisplay from '../components/QRCode';
import OtpVerificationToggle from '../components/OtpVerificationToggle';
import { generateSystemReferenceNumber } from '../utils/systemReferenceNumber';
import toastService from '../utils/toastService';
import { getIndustryLabel } from '../constants/workflowOptions';
import { FIELD_LIMITS } from '../utils/constants';
import { validateTextField } from '../utils/validation';
import { resolveCustomerReferenceLabel } from '../utils/customerReferenceLabel';
import { formatDateTime } from '../utils/dateTime';
import filesIcon from '../assets/files.svg';
import scannerIcon from '../assets/scanner.svg';

const typeIcons = { photo: Camera, video: Video, identifier: Hash };

const ERR_EXPIRY_MINUTES = 'Expiry must be at least 15 minutes';
const ERR_EXPIRY_HOURS = 'Expiry must be at least 1 hour';
const ERR_EXPIRY_DAYS = 'Expiry must be at least 1 day';

// Named constants 
const MS_PER_HOUR = 3600000;
const COPY_RESET_DELAY_MS = 2000;
const PHONE_E164_LENGTH = 11;
const PHONE_AREA_CODE_END = 3;
const PHONE_PREFIX_END = 6;
const PHONE_DIGITS_REQUIRED = 10;
const MIN_EXPIRY_MINUTES = process.env.REACT_APP_MIN_EXPIRY_MINUTES ? parseInt(process.env.REACT_APP_MIN_EXPIRY_MINUTES, 10) : 15;
const MAX_EXPIRY_HOURS = 8760;
const MAX_EXPIRY_DAYS = 365;
const MAX_EXPIRY_MINUTES = 525600;

// Shared UI literals (Sonar: S1192)
const CLASS_FIELD_INPUT = 'w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content placeholder-base-content/20 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all';
const CLASS_FIELD_ERROR_BORDER = 'border-error focus:ring-error focus:border-error';
const CLASS_FIELD_DEFAULT_BORDER = 'border-base-300';
const CLASS_INFO_TOOLTIP = 'tooltip tooltip-right z-[100] before:bg-[#fffdf2] before:text-slate-800 before:border before:border-amber-200 before:shadow-2xl before:rounded-xl before:px-3 before:py-2 before:font-medium before:text-xs before:w-max before:max-w-[240px] before:content-[attr(data-tip)] before:whitespace-normal before:text-left before:leading-snug after:border-r-[#fffdf2]';

const TIME_UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' }
];

const formatExpiryLabel = (value, unit) => {
  if (!value || !unit) { return '—'; }

  switch (unit) {
    case 'minutes':
      return `${value} minute${value !== 1 ? 's' : ''}`;
    case 'hours':
      return `${value} hour${value !== 1 ? 's' : ''}`;
    case 'days':
      return `${value} day${value !== 1 ? 's' : ''}`;
    default:
      return `${value} ${unit}`;
  }
};

const convertToHours = (value, unit) => {
  switch (unit) {
    case 'minutes':
      return value / 60;
    case 'hours':
      return value;
    case 'days':
      return value * 24;
    default:
      return value;
  }
};

const getMaxExpiryForUnit = (unit) => {
  switch (unit) {
    case 'minutes': return MAX_EXPIRY_MINUTES;
    case 'days': return MAX_EXPIRY_DAYS;
    case 'hours':
    default: return MAX_EXPIRY_HOURS;
  }
};

const NewAssignment = () => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState('');
  const [workflowPreview, setWorkflowPreview] = useState(null);
  const [effectiveExpiry, setEffectiveExpiry] = useState(null);
  const [previewStep, setPreviewStep] = useState(0);
  const [submitterName, setSubmitterName] = useState('');
  const [submitterPhone, setSubmitterPhone] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [systemReferenceNumber, setSystemReferenceNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [assigneeName, setAssigneeName] = useState('');
  const [sendFailed, setSendFailed] = useState(false);
  const [customerReferenceNumber, setCustomerReferenceNumber] = useState('');
  const [workflowIndustryLogoUrls, setWorkflowIndustryLogoUrls] = useState({
    general: null,
    aviation: null,
    insurance: null,
    automotive: null,
    fieldServices: null,
  });

  const [customValue, setCustomValue] = useState('');
  const [customUnit, setCustomUnit] = useState('hours');
  const [restrictToSingleSession, setRestrictToSingleSession] = useState(false);
  const [otpVerification, setOtpVerification] = useState(false);
  const [otpVia, setOtpVia] = useState('email');
  const [offlineSupportEnabled, setOfflineSupportEnabled] = useState(true);
  const [tenantSettings, setTenantSettings] = useState(null);
  const [isWorkflowDropdownOpen, setIsWorkflowDropdownOpen] = useState(false);
  const [workflowSearch, setWorkflowSearch] = useState('');
  const [workflowSearchLoading, setWorkflowSearchLoading] = useState(false);
  const [selectedWorkflowName, setSelectedWorkflowName] = useState('');
  const workflowRef = useRef(null);
  const workflowSearchRef = useRef(null);
  const workflowDebounceRef = useRef(null);
  const [validationFocusTarget, setValidationFocusTarget] = useState(null);

  const FIELD_ID_MAP = {
    selectedWorkflow: null, // handled via workflowRef
    assigneeName: 'creator-name',
    submitterName: 'recipient-name',
    customerReferenceNumber: 'customer-ref',
    submitterPhone: 'recipient-phone',
    submitterEmail: 'new-assn-recipient-email',
  };

  useEffect(() => {
    if (!validationFocusTarget) { return; }
    const timer = requestAnimationFrame(() => {
      if (validationFocusTarget === 'selectedWorkflow') {
        workflowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setValidationFocusTarget(null);
        return;
      }
      const el = document.getElementById(FIELD_ID_MAP[validationFocusTarget]);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
        setValidationFocusTarget(null);
      }
    });
    return () => cancelAnimationFrame(timer);
  }, [validationFocusTarget]);
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(
    workflows.find((workflow) => workflow.id === selectedWorkflow)
  );

  useEffect(() => {
    setSystemReferenceNumber(generateSystemReferenceNumber());
    apiClient.get('/settings')
      .then(r => {
        const settings = r.data;
        if (settings) {
          setTenantSettings(settings);
          setWorkflowIndustryLogoUrls(settings.industryLogoUrls ? settings.industryLogoUrls : {
            general: null,
            aviation: null,
            insurance: null,
            automotive: null,
            fieldServices: null,
          });
          if (settings.otpVerification !== undefined) {
            setOtpVerification(!!settings.otpVerification);
          }
          if (settings.sendOtpVia) {
            setOtpVia(settings.sendOtpVia);
          }
        }
      })
      .catch(() => {
        // Silently fallback if unable to fetch settings
      });
  }, []);

  const fetchWorkflowOptions = useCallback(async (search = '') => {
    setWorkflowSearchLoading(true);
    try {
      const params = { limit: 50 };
      if (search.length >= 3) params.search = search;
      const r = await apiClient.get('/workflows', { params });
      const data = r.data;
      if (Array.isArray(data)) setWorkflows(data.sort((a, b) => a.name.localeCompare(b.name)));
      else if (data && Array.isArray(data.workflows)) setWorkflows(data.workflows.sort((a, b) => a.name.localeCompare(b.name)));
      else setWorkflows([]);
    } catch {
      setWorkflows([]);
    } finally {
      setWorkflowSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isWorkflowDropdownOpen && workflows.length === 0 && workflowSearch.trim().length === 0) {
      fetchWorkflowOptions();
    }
  }, [isWorkflowDropdownOpen]);

  useEffect(() => {
    if (workflowDebounceRef.current) clearTimeout(workflowDebounceRef.current);
    const trimmed = workflowSearch.trim();
    if (trimmed.length === 0) { fetchWorkflowOptions(''); return; }
    if (trimmed.length < 3) { setWorkflows([]); return; }
    workflowDebounceRef.current = setTimeout(() => fetchWorkflowOptions(trimmed), 500);
    return () => clearTimeout(workflowDebounceRef.current);
  }, [workflowSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (workflowRef.current && !workflowRef.current.contains(event.target)) {
        setIsWorkflowDropdownOpen(false);
        setWorkflowSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!selectedWorkflow) {
      setWorkflowPreview(null);
      setEffectiveExpiry(null);
      setPreviewStep(0);
      setCustomValue('');
      setCustomUnit('hours');
      return;
    }
    const wf = workflows.find(w => w.id === selectedWorkflow);
    setWorkflowPreview(wf || null);
    setPreviewStep(0);

    if (wf?.defaultExpiry && wf?.expiryTimeUnit) {
      const hours = convertToHours(wf.defaultExpiry, wf.expiryTimeUnit);
      setEffectiveExpiry({
        hours,
        source: 'workflow',
        value: wf.defaultExpiry,
        unit: wf.expiryTimeUnit
      });
      // Populate custom expiry fields with workflow values
      setCustomValue(String(wf.defaultExpiry));
      setCustomUnit(wf.expiryTimeUnit);
    } else if (wf?.defaultExpiryHours) {
      // Backwards compatibility
      setEffectiveExpiry({
        hours: wf.defaultExpiryHours,
        source: 'workflow',
        value: wf.defaultExpiryHours,
        unit: 'hours'
      });
      // Populate custom expiry fields with workflow values
      setCustomValue(String(wf.defaultExpiryHours));
      setCustomUnit('hours');
    } else {
      apiClient.get('/settings')
        .then(r => {
          const settings = r.data;
          setTenantSettings(settings);
          let hours = 24;
          let value = 24;
          let unit = 'hours';

          if (settings.defaultExpiry && settings.expiryTimeUnit) {
            value = settings.defaultExpiry;
            unit = settings.expiryTimeUnit;
            hours = convertToHours(value, unit);
          } else if (settings.defaultExpiryHours) {
            // Backwards compatibility
            hours = settings.defaultExpiryHours;
            value = settings.defaultExpiryHours;
            unit = 'hours';
          } else {
            hours = 24;
            value = 24;
            unit = 'hours';
          }

          setEffectiveExpiry({ hours, source: 'tenant', value, unit });
          // Populate custom expiry fields with tenant settings
          setCustomValue(String(value));
          setCustomUnit(unit);
        })
        .catch(() => {
          setEffectiveExpiry({
            hours: 24,
            source: 'default',
            value: 24,
            unit: 'hours'
          });
          // Default fallback
          setCustomValue('24');
          setCustomUnit('hours');
        });
    }
  }, [selectedWorkflow, workflows]);

  const sendAfterRef = useRef(false);

  const callMessageApi = async (assignmentId) => {
    return apiClient.post(`/assignments/${assignmentId}/send-link`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let hasErrors = false;
    const currentErrors = {};

    if (!selectedWorkflow) {
      currentErrors.selectedWorkflow = 'Please select a capture workflow';
      hasErrors = true;
    }
    const assigneeError = validateTextField(assigneeName, 'Creator name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT });
    if (assigneeError) {
      currentErrors.assigneeName = assigneeError;
      hasErrors = true;
    } else {
      currentErrors.submitterName = '';
    }

    const submitterError = validateTextField(submitterName, 'Recipient name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT });
    if (submitterError) {
      currentErrors.submitterName = submitterError;
      hasErrors = true;
    }

    const crnError = validateTextField(customerReferenceNumber, dynamicCustomerReferenceLabel, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.REFERENCE });
    if (crnError) {
      currentErrors.customerReferenceNumber = crnError;
      hasErrors = true;
    } else {
      currentErrors.customerReferenceNumber = '';
    }

    if (otpVerification && otpVia === 'sms') {
      const digits = submitterPhone.replace(/[^\d]/g, '');
      if (!submitterPhone || digits.length !== 10) {
        currentErrors.submitterPhone = 'Phone number is required and must be 10 digits for SMS OTP verification';
        hasErrors = true;
      }
    } else if (submitterPhone) {
      const digits = submitterPhone.replace(/[^\d]/g, '');
      if (digits.length > 0 && digits.length !== 10) {
        currentErrors.submitterPhone = 'Phone number must be 10 digits';
        hasErrors = true;
      }
    }

    if (otpVerification && otpVia === 'email') {
      if (!submitterEmail) {
        currentErrors.submitterEmail = 'Email is required for Email OTP verification';
        hasErrors = true;
      } else if (submitterEmail.length > FIELD_LIMITS.EMAIL) {
        currentErrors.submitterEmail = `Email cannot exceed ${FIELD_LIMITS.EMAIL} characters`;
        hasErrors = true;
      } else if (!isEmail(submitterEmail)) {
        currentErrors.submitterEmail = 'Invalid email format';
        hasErrors = true;
      }
    } else if (submitterEmail) {
      if (submitterEmail.length > FIELD_LIMITS.EMAIL) {
        currentErrors.submitterEmail = `Email cannot exceed ${FIELD_LIMITS.EMAIL} characters`;
        hasErrors = true;
      } else if (!isEmail(submitterEmail)) {
        currentErrors.submitterEmail = 'Invalid email format';
        hasErrors = true;
      }
    }

    setFieldErrors(currentErrors);
    if (hasErrors) {
      const firstErrorKey = ['selectedWorkflow', 'assigneeName', 'submitterName', 'customerReferenceNumber', 'submitterPhone', 'submitterEmail'].find(k => currentErrors[k]);
      if (firstErrorKey) { setValidationFocusTarget(firstErrorKey); }
      return;
    }

    setError('');
    setLoading(true);
    try {
      const payload = {
        workflowId: selectedWorkflow,
        submitterName,
        submitterPhone: submitterPhone || undefined,
        submitterEmail: submitterEmail || undefined,
        systemReferenceNumber: systemReferenceNumber.trim() || undefined,
        assigneeName: assigneeName.trim() || undefined,
        customerReferenceNumber: customerReferenceNumber.trim() || undefined,
        requiredOtpVerification: otpVerification,
        otpVia: otpVerification ? otpVia : undefined,
      };

      const submitExpiry = parseInt(customValue, 10);
      const effectiveUnit = customUnit;

      if (isNaN(submitExpiry) || submitExpiry <= 0) {
        setError('Please enter a valid positive number for expiry');
        setLoading(false);
        return;
      }
      if (effectiveUnit === 'minutes' && submitExpiry < MIN_EXPIRY_MINUTES) {
        setError(ERR_EXPIRY_MINUTES);
        setLoading(false);
        return;
      }
      if (effectiveUnit === 'hours' && submitExpiry < 1) {
        setError(ERR_EXPIRY_HOURS);
        setLoading(false);
        return;
      }
      if (effectiveUnit === 'days' && submitExpiry < 1) {
        setError(ERR_EXPIRY_DAYS);
        setLoading(false);
        return;
      }

      const maxAllowed = getMaxExpiryForUnit(effectiveUnit);
      if (submitExpiry > maxAllowed) {
        setError(`Expiry cannot exceed ${maxAllowed} ${effectiveUnit}`);
        setLoading(false);
        return;
      }

      payload.defaultExpiry = submitExpiry;
      payload.expiryTimeUnit = effectiveUnit;
      payload.restrictToSingleSession = restrictToSingleSession;
      payload.offlineSupportEnabled = offlineSupportEnabled;

      const response = await apiClient.post('/assignments/create', payload);
      const assignmentData = response.data;
      setResult(assignmentData);

      // If triggered by "Generate & Send", call the send API with the returned link
      if (sendAfterRef.current) {
        try {
          await callMessageApi(assignmentData.assignment.id);
          setSendFailed(false);
          toastService.success('Assignment created and message sent successfully');
        } catch (sendErr) {
          setSendFailed(true);
          toastService.error('Assignment created, but failed to send message');
        }
      } else {
        setSendFailed(false);
        toastService.success('Assignment created successfully');
      }
    } catch (err) {
      const msg = err.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('. ') : (msg || err.response?.data?.error || 'Failed to create assignment'));
    } finally {
      setLoading(false);
      sendAfterRef.current = false;
    }
  };

  const handleRetrySend = async () => {
    if (!result || loading) { return; }

    setLoading(true);
    try {
      await callMessageApi(result.id || result.assignment?.id);
      setSendFailed(false);
      toastService.success('Message sent successfully');
    } catch (err) {
      toastService.error('Failed to resend message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result.captureLink);
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_RESET_DELAY_MS);
    } catch {
      const el = document.createElement('textarea');
      el.value = result.captureLink;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_RESET_DELAY_MS);
    }
  };

  const handleReset = () => {
    setResult(null);
    setSendFailed(false);
    setSelectedWorkflow('');
    setSubmitterName('');
    setSubmitterPhone('');
    setSubmitterEmail('');
    setSystemReferenceNumber(generateSystemReferenceNumber());
    setWorkflowPreview(null);
    setEffectiveExpiry(null);
    setPreviewStep(0);
    setFieldErrors({});
    setCustomValue('');
    setCustomUnit('hours');
    setRestrictToSingleSession(false);
    setOtpVerification(tenantSettings?.otpVerification || false);
    setOtpVia(tenantSettings?.sendOtpVia || 'email');
    setAssigneeName('');
    setCustomerReferenceNumber('');
  };

  const handleFieldBlur = useCallback((fieldKey, value, fieldLabel, options) => {
    const errorMsg = validateTextField(value, fieldLabel, options);
    setFieldErrors(prev => ({ ...prev, [fieldKey]: errorMsg }));
  }, []);

  const formatPhoneNumber = (value) => {
    let phoneNumber = value.replace(/[^\d]/g, '');
    if (!phoneNumber) { return ''; }
    // Strip leading country code '1' when pasting E.164 (+17327979714 → 7327979714)
    if (phoneNumber.length === PHONE_E164_LENGTH && phoneNumber.startsWith('1')) {
      phoneNumber = phoneNumber.slice(1);
    }
    if (phoneNumber.length < 4) { return phoneNumber; }
    if (phoneNumber.length < 7) { return `(${phoneNumber.slice(0, PHONE_AREA_CODE_END)}) ${phoneNumber.slice(PHONE_AREA_CODE_END)}`; }
    return `(${phoneNumber.slice(0, PHONE_AREA_CODE_END)}) ${phoneNumber.slice(PHONE_AREA_CODE_END, PHONE_PREFIX_END)}-${phoneNumber.slice(PHONE_PREFIX_END, PHONE_DIGITS_REQUIRED)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setSubmitterPhone(formatted);

    if (formatted) {
      // Extract only digits from the formatted phone number
      const digits = formatted.replace(/[^\d]/g, '');
      if (digits.length > 0 && digits.length !== 10) {
        setFieldErrors(prev => ({ ...prev, submitterPhone: 'Phone number must be 10 digits' }));
      } else {
        setFieldErrors(prev => ({ ...prev, submitterPhone: '' }));
      }
    } else {
      // Clear error if field is empty (since it's optional)
      setFieldErrors(prev => ({ ...prev, submitterPhone: '' }));
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setSubmitterEmail(value);
    if (value) {
      if (value.length > FIELD_LIMITS.EMAIL) {
        setFieldErrors(prev => ({ ...prev, submitterEmail: `Email cannot exceed ${FIELD_LIMITS.EMAIL} characters` }));
      } else if (!isEmail(value)) {
        setFieldErrors(prev => ({ ...prev, submitterEmail: 'Invalid email format' }));
      } else {
        setFieldErrors(prev => ({ ...prev, submitterEmail: '' }));
      }
    } else {
      setFieldErrors(prev => ({ ...prev, submitterEmail: '' }));
    }
  };

  if (result) {
    return (
      <div className="max-w-xl mx-auto py-8">
        <div className="bg-base-100 border border-base-300 rounded-3xl p-6 shadow-2xl overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-success to-emerald-400" />
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center">
              <CheckCircle className="text-primary" size={28} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-base-content tracking-tight uppercase">Assignment Created</h2>
              <p className="text-xs text-base-content/40 font-bold tracking-widest uppercase">Media session is live and ready</p>
            </div>
          </div>

          <div className="space-y-3 mb-4 bg-base-200/50 p-4 rounded-2xl border border-base-300/50">
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-black text-base-content/30 uppercase tracking-[0.2em]">System Reference</span>
              <span className="font-black text-base-content tracking-tight truncate w-[200px]">{result.assignment.systemReferenceNumber || '—'}</span>
            </div>
            {result.assignment.assigneeName && (
              <div className="flex justify-between items-center py-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-black text-base-content/30 uppercase tracking-[0.2em]">Creator</span>
                  <div className={CLASS_INFO_TOOLTIP} data-tip="Person who creates and sends the assignment.">
                    <Info size={10} className="text-base-content/30 cursor-help" />
                  </div>
                </div>
                <span className="font-black text-base-content tracking-tight truncate w-[200px]">{result.assignment.assigneeName}</span>
              </div>
            )}
            {result.assignment.customerReferenceNumber && (
              <div className="flex justify-between gap-2 items-center py-1">
                <div className="min-w-0 flex-1">
                  <span className="text-[10px] text-center font-black text-base-content/30 tracking-[0.2em] custom-line-break" title={dynamicCustomerReferenceLabel}>
                    <span className='uppercase'>{dynamicCustomerReferenceLabel}{' '}</span>
                    <span className={`${CLASS_INFO_TOOLTIP} inline-block align-middle ml-1`} data-tip="Your own reference number for this assignment (e.g. policy number, claim number, or any identifier from your system)">
                      <Info size={10} className="text-base-content/30 cursor-help z-10" />
                    </span>
                  </span>
                </div>
                <span className="font-black text-base-content tracking-tight w-[200px] shrink-0 custom-line-break">{result.assignment.customerReferenceNumber}</span>
              </div>
            )}
            <div className="flex justify-between items-center py-1">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-black text-base-content/30 uppercase tracking-[0.2em]">Recipient</span>
                <div className={CLASS_INFO_TOOLTIP} data-tip="Person who gets the link and completes the assignment.">
                  <Info size={10} className="text-base-content/30 cursor-help" />
                </div>
              </div>
              <span className="font-black text-base-content tracking-tight truncate w-[200px]">{result.assignment.submitterName}</span>
            </div>
            <div className="flex justify-between items-center border-t border-base-content/5 pt-2 mt-2">
              <span className="text-[10px] font-black text-base-content/30 uppercase tracking-[0.2em]">Workflow</span>
              <span className="font-black text-base-content tracking-tight truncate w-[200px]">{result.assignment.workflowName}</span>
            </div>
            <div className="flex justify-between items-center border-t border-base-content/5 pt-2 mt-2">
              <span className="text-[10px] font-black text-base-content/30 uppercase tracking-[0.2em]">Expires</span>
              <span className="font-black text-base-content tracking-tight truncate w-[200px]">
                {formatDateTime(result.expiresAt)}
              </span>
            </div>
          </div>

          <div className="mb-8">
            <p className="text-[10px] font-black text-base-content/30 uppercase tracking-[0.2em] mb-3">Submission Link</p>
            <div className="bg-base-300/50 border border-base-300 rounded-xl px-4 py-4 text-xs text-base-content/80 custom-line-break font-mono mb-4 leading-relaxed">
              {result.captureLink}
            </div>
            <button
              onClick={handleCopy}
              className={`flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-black uppercase text-xs tracking-widest transition-all ${copied ? 'bg-success text-white' : 'bg-base-200 text-base-content hover:bg-base-300'}`}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'Copied to Clipboard!' : 'Copy Link'}
            </button>
          </div>

          {result?.subAccountShowQrCode !== false && (
            <div className="flex flex-col items-center gap-4 p-4 bg-base-200/30 rounded-2xl border border-base-300/50 mb-4">
              <QRCodeDisplay value={result.captureLink} size={160} />
              <p className="text-[10px] font-black text-base-content/40 text-center uppercase tracking-widest leading-relaxed truncate w-[320px]">
                Share link with <span className="text-primary truncate">{result.assignment.submitterName}</span> to begin
              </p>
            </div>
          )}

          {sendFailed && (
            <button
              onClick={handleRetrySend}
              disabled={loading}
              className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl border border-primary/20 bg-primary/5 text-primary font-bold text-sm tracking-wide hover:bg-primary/10 transition-all mb-4 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Retry Send Message'}
            </button>
          )}

          <button
            onClick={handleReset}
            className="btn btn-premium w-full"
          >
            Create Another Assignment
          </button>
        </div>
      </div>
    );
  }

  const items = workflowPreview?.items || [];
  const branding = workflowPreview?.branding || {};
  const totalScreens = 2 + items.length + 1; // welcome + instructions + steps + done
  const screenLabels = ['Welcome', 'Steps', ...items.map((_, i) => `#${i + 1}`), 'Done'];

  const rawWelcomeTitle = workflowPreview?.branding?.welcomeTitle || 'Welcome, {name}'
  const welcomeTitle = rawWelcomeTitle?.replaceAll('{name}', submitterName);

  // Calculate the expiry date
  const expiresAtDate = new Date(
    Date.now() +
    convertToHours(parseInt(customValue, 10), customUnit) * MS_PER_HOUR
  );

  return (
    <div className="flex flex-col lg:flex-row h-auto lg:h-[calc(100vh-3rem)] lg:overflow-hidden rounded-2xl border border-base-300 shadow-sm bg-base-100">
      {/* Left: Main Content */}
      <div className="flex-1 flex flex-col overflow-y-auto bg-base-100/50 backdrop-blur-md border-b lg:border-b-0 lg:border-r border-base-300">
        <div className="p-6 sm:p-8 border-b border-base-300 flex-shrink-0 flex justify-between items-start">
          <div>
            <h1 className="page-title mb-1 flex items-baseline gap-2 min-w-0 max-w-full overflow-hidden">
              <span className="shrink-0">New Assignment</span>
              {workflowPreview && (
                <span className="text-primary/60 font-normal text-2xl custom-line-break flex-1 min-w-0"> • {workflowPreview.name}</span>
              )}
            </h1>
            <p className="page-subtitle">Setup a secure media collection session</p>
          </div>
          <button
            onClick={() => navigate('/assignments')}
            className="p-2 hover:bg-base-200 rounded-xl transition-colors text-base-content/40 hover:text-base-content"
            title="Close and return to assignments"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 sm:p-8 flex-1">
          <div className="flex flex-col xl:flex-row gap-8">
            {/* Form Column */}
            <div className="w-full xl:w-1/2 order-2 xl:order-1">
              <form onSubmit={handleSubmit} noValidate className="space-y-6">

                <div className="relative" ref={workflowRef}>
                  <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
                    Select Capture Workflow *
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsWorkflowDropdownOpen(!isWorkflowDropdownOpen)}
                    className="flex items-center justify-between gap-2 w-full rounded-xl px-4 py-3 bg-base-100 border border-base-300 text-base-content focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all cursor-pointer group min-w-0"
                  >
                    <span className="truncate flex-1 text-left min-w-0 block">
                      {selectedWorkflow ? selectedWorkflowName || 'Choose a workflow...' : 'Choose a workflow...'}
                    </span>
                    <ChevronRight size={14} className={`text-base-content/20 group-hover:text-primary transition-transform shrink-0 ${isWorkflowDropdownOpen ? 'rotate-90' : ''}`} />
                  </button>

                  {isWorkflowDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[200] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col max-h-64">
                      {/* Search bar */}
                      <div className="px-3 py-2 border-b border-base-content/10 flex-shrink-0">
                        <div className="relative">
                          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-base-content/30" />
                          <input
                            ref={workflowSearchRef}
                            type="text"
                            value={workflowSearch}
                            onChange={e => setWorkflowSearch(e.target.value)}
                            placeholder="Search workflows..."
                            className="w-full pl-7 pr-3 py-1.5 text-xs bg-base-200 border border-base-content/10 rounded-lg outline-none focus:border-primary text-base-content placeholder-base-content/30"
                            autoFocus
                          />
                          {workflowSearch && (
                            <button type="button" onClick={() => setWorkflowSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content">
                              <X size={11} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="overflow-y-auto no-scrollbar flex-1">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedWorkflow('');
                            setIsWorkflowDropdownOpen(false);
                            setWorkflowSearch('');
                          }}
                          className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 flex items-center gap-2 group ${!selectedWorkflow ? 'bg-primary/5' : ''}`}
                        >
                          <span className={`text-[10px] font-black uppercase tracking-widest ${!selectedWorkflow ? 'text-primary' : 'text-base-content/30 group-hover:text-primary'}`}>Choose a workflow...</span>
                          {!selectedWorkflow && <CheckCircle size={12} className="text-primary ml-auto" />}
                        </button>

                        {workflowSearch.trim().length > 0 && workflowSearch.trim().length < 3 ? (
                          <div className="px-4 py-6 text-center">
                            <p className="text-[10px] font-black text-base-content/20 uppercase tracking-widest">Type at least 3 characters to search</p>
                          </div>
                        ) : workflows.length === 0 && !workflowSearchLoading ? (
                          <div className="px-4 py-6 text-center">
                            <p className="text-[10px] font-black text-base-content/20 uppercase tracking-widest">No workflows found</p>
                          </div>
                        ) : (
                          workflows.map((w) => (
                            <button
                              key={w.id}
                              type="button"
                              onClick={() => {
                                setSelectedWorkflow(w.id);
                                setSelectedWorkflowName(w.name);
                                setIsWorkflowDropdownOpen(false);
                                setWorkflowSearch('');
                                setFieldErrors(prev => ({ ...prev, selectedWorkflow: '' }));
                              }}
                              className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 last:border-none flex items-center gap-2 group ${selectedWorkflow === w.id ? 'bg-primary/5' : ''}`}
                            >
                              <div className="flex flex-col min-w-0 flex-1">
                                <span
                                  className={`text-sm font-bold custom-truc ${selectedWorkflow === w.id
                                    ? 'text-primary'
                                    : 'text-base-content group-hover:text-primary'
                                    }`}
                                >
                                  {w.name}
                                </span>
                              </div>

                              {selectedWorkflow === w.id && (
                                <CheckCircle size={12} className="text-primary ml-auto" />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                  {/* Keep hidden input for form validation if needed, though we check selectedWorkflow in handleSubmit */}
                  <input type="hidden" value={selectedWorkflow} required name="workflowId" />
                  {fieldErrors.selectedWorkflow && (
                    <p className="text-error text-xs mt-1 font-medium">{fieldErrors.selectedWorkflow}</p>
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="creator-name" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                      Creator Name *
                    </label>
                    <div className={CLASS_INFO_TOOLTIP} data-tip="Person who creates and sends the assignment.">
                      <Info size={10} className="text-base-content/30 cursor-help" />
                    </div>
                  </div>
                  <input
                    id="creator-name"
                    type="text"
                    value={assigneeName}
                    onChange={e => {
                      setAssigneeName(e.target.value);
                      if (fieldErrors.assigneeName) {
                        setFieldErrors(prev => ({ ...prev, assigneeName: validateTextField(e.target.value, 'Creator name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT }) }));
                      }
                    }}
                    onBlur={() => handleFieldBlur('assigneeName', assigneeName, 'Creator name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT })}
                    placeholder="e.g. Agent 007"
                    className={`${CLASS_FIELD_INPUT} ${fieldErrors.assigneeName ? CLASS_FIELD_ERROR_BORDER : CLASS_FIELD_DEFAULT_BORDER}`}
                  />
                  {fieldErrors.assigneeName && (
                    <p className="text-error text-xs mt-1 font-medium">{fieldErrors.assigneeName}</p>
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="recipient-name" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                      Recipient Name *
                    </label>
                    <div className={CLASS_INFO_TOOLTIP} data-tip="Person who gets the link and completes the assignment.">
                      <Info size={10} className="text-base-content/30 cursor-help" />
                    </div>
                  </div>
                  <input
                    id="recipient-name"
                    type="text"
                    value={submitterName}
                    onChange={e => {
                      setSubmitterName(e.target.value);
                      if (fieldErrors.submitterName) {
                        setFieldErrors(prev => ({ ...prev, submitterName: validateTextField(e.target.value, 'Recipient name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT }) }));
                      }
                    }}
                    onBlur={() => handleFieldBlur('submitterName', submitterName, 'Recipient name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT })}
                    placeholder="e.g. John Smith"
                    className={`${CLASS_FIELD_INPUT} ${fieldErrors.submitterName
                      ? CLASS_FIELD_ERROR_BORDER
                      : CLASS_FIELD_DEFAULT_BORDER
                      }`}
                  />
                  {fieldErrors.submitterName && (
                    <p className="text-error text-xs mt-1 font-medium">{fieldErrors.submitterName}</p>
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="customer-ref" className="text-[10px] font-black text-base-content/40 tracking-[0.2em] block" title={dynamicCustomerReferenceLabel}>
                      <span className="uppercase">{dynamicCustomerReferenceLabel} *</span>
                      <div className={`${CLASS_INFO_TOOLTIP} ml-2`} data-tip="Your own reference number for this assignment (e.g. policy number, claim number, or any identifier from your system)">
                        <Info size={10} className="text-base-content/30 cursor-help" />
                      </div>
                    </label>
                  </div>
                  <input
                    id="customer-ref"
                    type="text"
                    value={customerReferenceNumber}
                    onChange={e => {
                      setCustomerReferenceNumber(e.target.value);
                      if (fieldErrors.customerReferenceNumber) {
                        setFieldErrors(prev => ({ ...prev, customerReferenceNumber: validateTextField(e.target.value, dynamicCustomerReferenceLabel, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.REFERENCE }) }));
                      }
                    }}
                    onBlur={() => handleFieldBlur('customerReferenceNumber', customerReferenceNumber, dynamicCustomerReferenceLabel, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.REFERENCE })}
                    placeholder="e.g. POL-123456"
                    className={`${CLASS_FIELD_INPUT} ${CLASS_FIELD_DEFAULT_BORDER}`}
                  />
                  {fieldErrors.customerReferenceNumber && (
                    <p className="text-error text-xs mt-1 font-medium">{fieldErrors.customerReferenceNumber}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="recipient-phone" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
                    Recipient Phone{otpVerification && otpVia === 'sms' ? ' *' : ''}
                  </label>
                  <input
                    id="recipient-phone"
                    type="tel"
                    value={submitterPhone}
                    onChange={handlePhoneChange}
                    placeholder="(555) 000-0000"
                    maxLength={14}
                    className={`${CLASS_FIELD_INPUT} ${fieldErrors.submitterPhone
                      ? CLASS_FIELD_ERROR_BORDER
                      : CLASS_FIELD_DEFAULT_BORDER
                      }`}
                  />
                  {fieldErrors.submitterPhone && (
                    <p className="text-error text-xs mt-1 font-medium">{fieldErrors.submitterPhone}</p>
                  )}

                </div>

                <div>
                  <label htmlFor="new-assn-recipient-email" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
                    Recipient Email{otpVerification && otpVia === 'email' ? ' *' : ''}
                  </label>
                  <input
                    id="new-assn-recipient-email"
                    type="email"
                    value={submitterEmail}
                    onChange={handleEmailChange}
                    placeholder="name@company.com"
                    className={`${CLASS_FIELD_INPUT} ${fieldErrors.submitterEmail
                      ? CLASS_FIELD_ERROR_BORDER
                      : CLASS_FIELD_DEFAULT_BORDER
                      }`}
                  />
                  {fieldErrors.submitterEmail && (
                    <p className="text-error text-xs mt-1 font-medium">{fieldErrors.submitterEmail}</p>
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="system-ref" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                      System Reference Number
                    </label>
                    <div className={CLASS_INFO_TOOLTIP} data-tip="Auto-generated unique identifier for internal tracking purposes">
                      <Info size={10} className="text-base-content/30 cursor-help" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <input
                      id="system-ref"
                      type="text"
                      value={systemReferenceNumber}
                      readOnly
                      placeholder="#CLM-2024-143052"
                      className="w-full px-4 py-3 bg-base-200 border border-base-300 rounded-xl text-base-content/70 outline-none cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* Custom Expiry Section */}
                <div>
                  <label htmlFor="expiry-value" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
                    Link Expiry
                  </label>

                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="flex items-center gap-2">
                      <input
                        id="expiry-value"
                        type="number"
                        min="1"
                        max="8760"
                        value={customValue}
                        onWheel={(e) => e.target.blur()}
                        onChange={e => setCustomValue(e.target.value)}
                        placeholder="Enter value"
                        className="rounded-md w-24 pl-3 pr-2 py-2.5 bg-base-100 border border-base-300 text-sm font-medium focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                      />
                      <select
                        value={customUnit}
                        onChange={e => setCustomUnit(e.target.value)}
                        className="rounded-md pl-3 pr-8 py-2.5 bg-base-100 border border-base-300 text-sm font-medium focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                      >
                        {TIME_UNITS.map(unit => (
                          <option key={unit.value} value={unit.value}>{unit.label}</option>
                        ))}
                      </select>
                    </div>
                    {customValue && parseInt(customValue, 10) > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-primary/5 border border-primary/10 rounded-md">
                        <span className="text-[14px] font-black text-base-content/30 uppercase">≈</span>
                        <span className="text-[10px] text-primary font-black uppercase tracking-wider">
                          {formatExpiryLabel(parseInt(customValue, 10), customUnit)}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] font-black text-base-content/20 uppercase tracking-widest mt-2 ml-1">
                    Values auto-populated from {effectiveExpiry?.source === 'workflow' ? 'workflow' : 'tenant'} settings. Modify as needed.
                  </p>
                </div>

                {/* Single-Session Restriction Toggle */}
                <div className="flex items-center justify-between px-4 py-3.5 bg-base-200/50 border border-base-300 rounded-xl">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">
                      Restrict to Single Session
                    </span>
                    <div
                      className="tooltip tooltip-right z-[100] before:bg-[#fffdf2] before:text-slate-800 before:border before:border-amber-200 before:shadow-2xl before:rounded-xl before:px-3 before:py-2 before:font-medium before:text-xs before:max-w-none before:content-[attr(data-tip)] before:whitespace-nowrap after:border-r-[#fffdf2]"
                      data-tip="When enabled, only one device can actively use this capture link at a time. A conflict modal is shown if another device tries to open it."
                    >
                      <Info size={10} className="text-base-content/30 cursor-help" />
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      id="restrict-single-session"
                      type="checkbox"
                      className="sr-only peer"
                      checked={restrictToSingleSession}
                      onChange={(e) => setRestrictToSingleSession(e.target.checked)}
                    />
                    <div className="relative w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
                  </label>
                </div>

                <OtpVerificationToggle
                  toggleId="otp-verification"
                  checked={otpVerification}
                  onToggleChange={setOtpVerification}
                  via={otpVia}
                  onViaChange={setOtpVia}
                />

                <div className="flex items-center justify-between px-4 py-3.5 bg-base-200/50 border border-base-300 rounded-xl">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">
                      Offline Support
                    </span>
                    <div
                      className="tooltip tooltip-right z-[100] before:bg-[#fffdf2] before:text-slate-800 before:border before:border-amber-200 before:shadow-2xl before:rounded-xl before:px-3 before:py-2 before:font-medium before:text-xs before:max-w-none before:content-[attr(data-tip)] before:whitespace-nowrap after:border-r-[#fffdf2]"
                      data-tip="When enabled, the capture UI keeps working without connectivity and syncs automatically once back online. Disable only if this recipient must never store capture data locally."
                    >
                      <Info size={10} className="text-base-content/30 cursor-help" />
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      id="offline-support-enabled"
                      type="checkbox"
                      className="sr-only peer"
                      checked={offlineSupportEnabled}
                      onChange={(e) => setOfflineSupportEnabled(e.target.checked)}
                    />
                    <div className="relative w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
                  </label>
                </div>

                {
                  error && (
                    <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-xl text-sm font-bold">{error}</div>
                  )
                }

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  {/* Generate Secure Link */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 min-h-[56px] px-5 rounded-2xl bg-primary text-primary-content font-bold text-sm tracking-wide shadow-sm hover:scale-[1.01] hover:shadow-md transition-all disabled:opacity-50 disabled:hover:scale-100"
                  >
                    {loading ? 'Processing...' : 'Generate Secure Link'}
                  </button>

                  {/* Generate & Send - Only show if phone number is provided */}
                  {submitterPhone && (
                    <button
                      type="submit"
                      disabled={loading}
                      onClick={() => {
                        sendAfterRef.current = true;
                      }}
                      className="flex-1 min-h-[56px] px-5 rounded-2xl border border-primary/20 bg-primary/5 text-primary font-bold text-sm tracking-wide hover:bg-primary/10 hover:scale-[1.01] transition-all disabled:opacity-50 disabled:hover:scale-100"
                    >
                      {loading ? 'Processing...' : 'Generate & Send Message'}
                    </button>
                  )}

                </div>

                {/* Cancel */}
                <button
                  type="button"
                  onClick={() => navigate('/assignments')}
                  className="w-full  min-h-[56px] px-5 rounded-2xl border border-base-300 bg-base-100 text-base-content/70 font-semibold text-sm hover:bg-base-200 transition-all"
                >
                  Cancel
                </button>
              </form >
            </div >

            {/* Workflow Preview Column */}
            <div className="w-full xl:w-1/2 order-1 xl:order-2">
              {workflowPreview && items.length > 0 && (
                <div className="border border-primary/20 bg-primary/5 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">
                      Template Preview
                    </p>
                    <span className="text-[10px] font-black text-primary/60 uppercase tracking-widest bg-primary/10 px-2 py-1 rounded-lg">
                      {items.length} Screens &bull; {getIndustryLabel(workflowPreview.industry)}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {items.map((item, index) => {
                      const Icon = typeIcons[item.type] || Camera;
                      const typeColors = {
                        photo: 'bg-primary/10 text-primary',
                        video: 'bg-secondary/10 text-secondary',
                        // identifier: 'bg-accent/10 text-accent'
                      };
                      return (
                        <div
                          key={item.id}
                          onClick={() => setPreviewStep(2 + index)}
                          className="flex items-center gap-4 bg-base-100 rounded-xl p-3 border border-base-300 cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group"
                        >
                          <span className="text-xs text-base-content/20 font-black w-4 flex-shrink-0 text-center group-hover:text-primary/40">{(index + 1).toString().padStart(2, '0')}</span>
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${typeColors[item.type] || 'bg-base-200 text-base-content/40'}`}>
                            {item.type === 'identifier' ? <img src={scannerIcon} alt="" className='w-10 h-10 object-contain p-1' /> : item.type === 'file' ? <img src={filesIcon} alt="" className='w-10 h-10 object-contain p-2' /> : <Icon size={18} />}
                          </div>
                          <span className="text-sm text-base-content/80 font-black tracking-tight flex-1 custom-line-break">{item.description}</span>
                          {!item.required && (
                            <span className="text-[9px] font-black uppercase tracking-widest text-base-content/20 bg-base-200 px-2.5 py-1 rounded-full flex-shrink-0">Optional</span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {workflowPreview.description && (
                    <p className="text-xs text-primary/60 mt-4 font-bold tracking-widest px-4 custom-line-break">{workflowPreview.description}</p>
                  )}

                  {(effectiveExpiry || (customValue && parseInt(customValue, 10) > 0)) && (
                    <div className="flex items-center gap-3 mt-6 pt-5 border-t border-primary/10">
                      <Clock size={14} className="text-primary/40 flex-shrink-0" />
                      <p className="text-[10px] font-black uppercase tracking-widest text-primary/40">
                        Link Lifetime: <span className="text-primary">
                          {customValue && parseInt(customValue, 10) > 0
                            ? formatExpiryLabel(parseInt(customValue, 10), customUnit)
                            : effectiveExpiry ? formatExpiryLabel(effectiveExpiry.value, effectiveExpiry.unit) : '—'
                          }
                        </span>
                        <span className="mx-2 opacity-50">&bull;</span>
                        {formatDateTime(expiresAtDate)}
                      </p>
                    </div>
                  )}
                </div>
              )
              }
            </div >
          </div >
        </div >
      </div>

      {/* Right: Phone Preview */}
      {
        workflowPreview ? (
          <div className="w-full lg:w-[360px] xl:w-[400px] flex-shrink-0 bg-base-200 flex flex-col border-t lg:border-t-0 border-base-300 overflow-y-auto overflow-x-hidden">
            <div className="px-4 py-3 border-b border-base-300 bg-base-100 flex justify-between flex-shrink-0">
              <p className="text-[10px] font-black text-base-content/30 uppercase tracking-[0.2em]">Live Preview</p>
              <p className="text-[10px] text-base-content/20 uppercase font-black tracking-widest">Interactive view</p>
            </div>

            {/* Screen selector */}
            <div className="px-3 py-2 bg-base-100 border-b border-base-300 flex-shrink-0">
              <div className="flex flex-wrap gap-2">
                {screenLabels.map((label, i) => (
                  <button
                    key={i}
                    onClick={() => setPreviewStep(i)}
                    className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${previewStep === i
                      ? 'text-white bg-primary shadow-lg scale-105'
                      : 'bg-base-200 text-base-content/40 hover:bg-base-300'
                      }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-2">
              <PhonePreview
                workflowName={workflowPreview.name}
                name={welcomeTitle}
                submitterName={submitterName}
                assigneeName={assigneeName}
                customerReferenceNumber={customerReferenceNumber}
                customerReferenceLabel={dynamicCustomerReferenceLabel}
                systemReferenceNumber={systemReferenceNumber}
                industry={workflowPreview.industry}
                description={workflowPreview.description}
                items={items}
                previewStep={previewStep}
                branding={branding}
                expiresAt={expiresAtDate}
                workflowIndustryLogoUrls={workflowIndustryLogoUrls}
                globalInstructionUrl={workflowPreview.globalInstructionUrl}
              />
            </div>

            {/* Navigation arrows */}
            <div className="px-5 py-1 bg-base-100 border-t border-base-300 flex items-center justify-between flex-shrink-0">
              <button
                onClick={() => setPreviewStep(p => Math.max(0, p - 1))}
                disabled={previewStep === 0}
                className="p-1.5 rounded-lg hover:bg-base-200 disabled:opacity-20 text-base-content transition"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-[10px] font-black text-base-content/20 uppercase tracking-widest">{previewStep + 1} / {Math.max(1, totalScreens)}</span>
              <button
                onClick={() => setPreviewStep(p => Math.min(totalScreens - 1, p + 1))}
                disabled={previewStep >= totalScreens - 1}
                className="p-1.5 rounded-lg hover:bg-base-200 disabled:opacity-20 text-base-content transition"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full lg:w-[360px] xl:w-[400px] flex-shrink-0 bg-base-200 flex flex-col items-center justify-center text-center p-8 border-t lg:border-t-0 border-base-300">
            <div className="text-5xl mb-6 opacity-40">📱</div>
            <p className="text-xs font-black text-base-content/40 uppercase tracking-[0.2em]">Select a workflow</p>
            <p className="text-[10px] text-base-content/20 mt-2 uppercase font-black tracking-widest">Preview will sync automatically</p>
          </div>
        )
      }
    </div >
  );
};

export default NewAssignment;
